import React, { useState, useRef, useEffect } from 'react';
import CustomDiffViewer from './CustomDiffViewer';
import MobileDiffViewer from './MobileDiffViewer';
import { XMarkIcon, ArrowsPointingOutIcon, ChevronDownIcon, ChevronRightIcon, ClipboardIcon } from '@heroicons/react/24/outline';

interface DiffViewerProps {
  isOpen: boolean;
  onClose: () => void;
  oldContent: string;
  newContent: string;
}

const DiffViewer: React.FC<DiffViewerProps> = ({ isOpen, onClose, oldContent, newContent }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 900, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [showDiffOnly, setShowDiffOnly] = useState(true);
  const [selectedSide, setSelectedSide] = useState<'left' | 'right' | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const diffViewerRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile(); // Initial check
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleDiffOnly = () => {
    setShowDiffOnly(!showDiffOnly);
  };


  // Handle mouse events to prevent cross-panel selection
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isFullscreen || isMobile) return;

    // Check if click is on diff content area
    const target = e.target as HTMLElement;
    const isDiffContent = target.closest('.diff-viewer');

    if (isDiffContent) {
      // Determine which side was clicked
      const rect = diffViewerRef.current?.getBoundingClientRect();
      if (rect) {
        const clickX = e.clientX - rect.left;
        const isLeftSide = clickX < rect.width / 2;
        setSelectedSide(isLeftSide ? 'left' : 'right');
      }
      return; // Don't start dragging if clicking on diff content
    }

    // Start dragging dialog
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Prevent text selection from crossing panels
  useEffect(() => {
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || !selectedSide || !diffViewerRef.current) return;

      const range = selection.getRangeAt(0);
      const rect = diffViewerRef.current.getBoundingClientRect();
      const middleX = rect.left + rect.width / 2;

      const isMouseOnLeft = e.clientX < middleX;

      // If mouse crosses to the other panel, constrain the selection
      if (selectedSide === 'left' && !isMouseOnLeft) {
        const leftPanel = diffViewerRef.current.querySelector('div[style*="border-right"]');
        if (leftPanel) {
          // Get the last text node in the left panel and set the selection end there
          const treeWalker = document.createTreeWalker(leftPanel, NodeFilter.SHOW_TEXT);
          let lastNode;
          while (treeWalker.nextNode()) lastNode = treeWalker.currentNode;
          if (lastNode) {
            range.setEnd(lastNode, lastNode.textContent?.length || 0);
          }
        }
      } else if (selectedSide === 'right' && isMouseOnLeft) {
        const rightPanel = diffViewerRef.current.querySelector('div:not([style*="border-right"])');
        if (rightPanel) {
          // Get the first text node in the right panel and set the selection start there
          const treeWalker = document.createTreeWalker(rightPanel, NodeFilter.SHOW_TEXT);
          const firstNode = treeWalker.nextNode();
          if (firstNode) {
            range.setStart(firstNode, 0);
          }
        }
      }
    };

    if (selectedSide) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [selectedSide]);


  const handleResizeMouseDown = (e: React.MouseEvent) => {
    if (isFullscreen || isMobile) return;
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && !isFullscreen && !isMobile) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      } else if (isResizing && !isFullscreen && !isMobile) {
        const newWidth = Math.max(400, resizeStart.width + (e.clientX - resizeStart.x));
        const newHeight = Math.max(300, resizeStart.height + (e.clientY - resizeStart.y));
        setSize({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, resizeStart, isFullscreen, isMobile]);

  // Center the dialog initially
  useEffect(() => {
    if (isOpen && !isFullscreen && !isDragging && !isResizing) {
      const centerX = (window.innerWidth - size.width) / 2;
      const centerY = (window.innerHeight - size.height) / 2;
      setPosition({ x: Math.max(0, centerX), y: Math.max(0, centerY) });
    }
  }, [isOpen, isFullscreen]);

  // Adjust position on window resize to keep it within bounds
  useEffect(() => {
    const handleResize = () => {
      if (!isFullscreen) {
        setPosition(prevPos => ({
          x: Math.max(0, Math.min(prevPos.x, window.innerWidth - size.width)),
          y: Math.max(0, Math.min(prevPos.y, window.innerHeight - size.height)),
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [size.width, size.height, isFullscreen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-auto">
      <div
        ref={dialogRef}
        className={`bg-white rounded-lg shadow-xl flex flex-col ${isFullscreen || isMobile
          ? 'fixed inset-[5%] w-[90%] h-[90%] max-w-none max-h-none md:inset-[5%] md:w-[90%] md:h-[90%] inset-0 w-full h-full'
          : 'absolute'
          }`}
        style={(!isFullscreen && !isMobile) ? {
          left: position.x,
          top: position.y,
          width: size.width,
          height: size.height,
          minWidth: 'min(400px, 90vw)',
          minHeight: '300px'
        } : (isMobile ? {
          // Force reset inline styles if previously set
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: 0,
          paddingTop: 'env(safe-area-inset-top, 0px)'
        } : {})}
      >
        <div
          className={`flex justify-between items-center p-4 border-b border-gray-200 flex-shrink-0 ${!isFullscreen && !isMobile ? 'cursor-move' : ''
            }`}
          onMouseDown={handleMouseDown}
        >
          <h2 className="text-lg font-semibold select-none truncate min-w-0 mr-2">AI Changes Made</h2>
          <div className="flex items-center space-x-2 flex-shrink-0">
            <button
              onClick={toggleDiffOnly}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-900 hover:bg-blue-200 rounded-md flex items-center space-x-1"
              title={showDiffOnly ? "Show all content" : "Show changes only"}
            >
              {showDiffOnly ? (
                <><ChevronDownIcon className="h-4 w-4" /><span>{isMobile ? 'All' : 'Show All'}</span></>
              ) : (
                <><ChevronRightIcon className="h-4 w-4" /><span>{isMobile ? 'Changes' : 'Changes Only'}</span></>
              )}
            </button>
            {!isMobile && (
              <button
                onClick={toggleFullscreen}
                className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                <ArrowsPointingOutIcon className="h-5 w-5" />
              </button>
            )}
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-700">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        <div className="overflow-auto flex-grow p-4">
          <div className="mb-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <span className="inline-block w-4 h-4 bg-red-100 border border-red-300 mr-2"></span>
              Removed content
              <span className="inline-block w-4 h-4 bg-green-100 border border-green-300 mr-2 ml-4"></span>
              Added content
            </div>
            <div className="text-xs text-gray-500">
              {selectedSide && `Selecting from ${selectedSide} panel`}
            </div>
          </div>
          <div
            ref={diffViewerRef}
            className="diff-viewer"
            onMouseDown={(e) => {
              // Only handle selection, not dragging
              const target = e.target as HTMLElement;
              if (target.closest('.diff-viewer')) {
                const rect = diffViewerRef.current?.getBoundingClientRect();
                if (rect) {
                  const clickX = e.clientX - rect.left;
                  const isLeftSide = clickX < rect.width / 2;
                  setSelectedSide(isLeftSide ? 'left' : 'right');
                }
              }
            }}
            style={{
              // Force equal column widths with CSS
              '--diff-viewer-table-layout': 'fixed',
              '--diff-viewer-column-width': '50%'
            } as React.CSSProperties}
          >
            {/* Desktop View (Side by Side) */}
            <div className="hidden md:block h-full w-full">
              <CustomDiffViewer oldContent={oldContent} newContent={newContent} showDiffOnly={showDiffOnly} />
            </div>

            {/* Mobile View (Unified) */}
            <div className="block md:hidden h-full w-full">
              <MobileDiffViewer oldContent={oldContent} newContent={newContent} showDiffOnly={showDiffOnly} />
            </div>
          </div>
        </div>

        {/* Resize handle - only show when not in fullscreen and not mobile */}
        {!isFullscreen && !isMobile && (
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-gray-300 hover:bg-gray-400 transition-colors"
            onMouseDown={handleResizeMouseDown}
            style={{
              background: 'linear-gradient(-45deg, transparent 30%, #9ca3af 30%, #9ca3af 40%, transparent 40%, transparent 60%, #9ca3af 60%, #9ca3af 70%, transparent 70%)'
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DiffViewer;
